import { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { UserPlus, Trash2, ShieldCheck, Crown, KeyRound, Copy } from 'lucide-react';
import { db, createOperator, resetOperatorPassword, deleteOperator, type OperatorRole } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';

interface Operator {
  email: string;
  name?: string;
  role?: OperatorRole;
  addedBy?: string;
  createdAt?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLE_LABEL: Record<OperatorRole, string> = { owner: 'Owner', manager: 'Manager', staff: 'Staff' };

export function Users() {
  const { user } = useAuth();
  const [ops, setOps] = useState<Operator[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<OperatorRole>('staff');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [tempPw, setTempPw] = useState<{ email: string; pw: string } | null>(null);

  useEffect(() => {
    return onSnapshot(
      collection(db, 'operators'),
      (snap) => setOps(snap.docs.map((d) => ({ email: d.id, ...(d.data() as Omit<Operator, 'email'>) }))),
      () => setOps([]),
    );
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 5000); }

  async function add() {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) return flash('Enter a valid email.');
    setBusy(true);
    try {
      const res = await createOperator({ email: e, name: name.trim(), role });
      const r = res.data;
      setEmail(''); setName(''); setRole('staff');
      if (r.created && r.tempPassword) {
        setTempPw({ email: e, pw: r.tempPassword });
        flash(r.emailed ? `Account created and login emailed to ${e}.` : `Account created. Email wasn't sent — share the password below.`);
      } else if (!r.created) {
        flash('That account already existed — access granted with the chosen role.');
      } else {
        flash('Account created.');
      }
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not create the account.');
    } finally { setBusy(false); }
  }

  async function changeRole(op: Operator, newRole: OperatorRole) {
    try {
      await updateDoc(doc(db, 'operators', op.email), { role: newRole, updatedAt: new Date().toISOString() });
      flash(`${op.email} is now ${ROLE_LABEL[newRole]}.`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not change role.'); }
  }

  async function resetPw(op: Operator) {
    setBusy(true);
    try {
      const res = await resetOperatorPassword({ email: op.email });
      flash(res.data.emailed ? `Reset link emailed to ${op.email}.` : 'Reset link created — see console/share manually.');
      if (!res.data.emailed && res.data.link) console.log('Password reset link:', res.data.link);
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not reset.'); }
    finally { setBusy(false); }
  }

  async function remove(op: Operator) {
    if (!confirm(`Remove ${op.email}? This deletes their login.`)) return;
    setBusy(true);
    try {
      await deleteOperator({ email: op.email });
      flash('Operator removed.');
    } catch (e) { flash(e instanceof Error ? e.message : 'Could not remove.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl text-espresso">User Management</h1>
      <p className="mt-1 font-body text-sm text-muted">
        Create staff accounts and set what they can do. New users receive their login by email and can change their password under My Account.
      </p>

      {/* Add */}
      <div className="mt-8 border border-line bg-paper p-6">
        <p className="eyebrow mb-4 flex items-center gap-2"><UserPlus size={15} /> Create an account</p>
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto]">
          <div>
            <label className="label">Email</label>
            <input className="field" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="thandi@gracehavenspa.co.bw" />
          </div>
          <div>
            <label className="label">Name</label>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Thandi" />
          </div>
          <div>
            <label className="label">Role</label>
            <select className="field" value={role} onChange={(e) => setRole(e.target.value as OperatorRole)}>
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>
        <p className="mt-3 font-body text-xs text-muted">
          <strong>Owner</strong>: full access incl. user management. <strong>Manager</strong>: everything except user management. <strong>Staff</strong>: bookings and invoices/receipts only — no totals or summaries.
        </p>
        <button onClick={add} disabled={busy} className="btn-primary mt-5"><UserPlus size={16} /> {busy ? 'Working…' : 'Create account'}</button>

        {tempPw && (
          <div className="mt-5 border border-gold/50 bg-gold/10 p-4">
            <p className="font-body text-sm text-espresso">Temporary password for <strong>{tempPw.email}</strong>:</p>
            <div className="mt-2 flex items-center gap-3">
              <code className="bg-cream px-3 py-1.5 font-mono text-sm text-espresso">{tempPw.pw}</code>
              <button onClick={() => { navigator.clipboard?.writeText(tempPw.pw); flash('Copied.'); }} className="inline-flex items-center gap-1 font-body text-xs text-gold-deep hover:underline"><Copy size={13} /> Copy</button>
              <button onClick={() => setTempPw(null)} className="ml-auto font-body text-xs text-muted hover:text-espresso">Dismiss</button>
            </div>
            <p className="mt-2 font-body text-xs text-muted">They should change it after first sign-in.</p>
          </div>
        )}
      </div>

      {/* List */}
      <div className="mt-8">
        <p className="eyebrow mb-3">Team</p>
        <div className="overflow-hidden border border-line bg-paper">
          {ops.slice().sort((a, b) => (a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0)).map((o) => {
            const isSelf = o.email === user?.email?.toLowerCase();
            return (
              <div key={o.email} className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3.5 last:border-0">
                <div className="flex items-center gap-3">
                  {o.role === 'owner' ? <Crown size={16} className="text-gold-deep" /> : <ShieldCheck size={16} className="text-gold-deep" />}
                  <div>
                    <p className="font-body text-sm font-semibold text-espresso">{o.name || o.email}{isSelf ? ' (you)' : ''}</p>
                    <p className="font-body text-xs text-muted">{o.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select value={o.role || 'staff'} disabled={isSelf} onChange={(e) => changeRole(o, e.target.value as OperatorRole)}
                    className="rounded-none border border-line bg-cream px-2 py-1.5 font-body text-xs text-espresso disabled:opacity-50">
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button onClick={() => resetPw(o)} disabled={busy} title="Send password reset" className="text-muted hover:text-gold-deep"><KeyRound size={16} /></button>
                  {!isSelf && <button onClick={() => remove(o)} disabled={busy} title="Remove" className="text-muted hover:text-blush-deep"><Trash2 size={16} /></button>}
                </div>
              </div>
            );
          })}
          {ops.length === 0 && <p className="px-5 py-6 text-center font-body text-sm text-muted">No operators yet.</p>}
        </div>
      </div>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
